import React, { useMemo } from 'react';
import { Box, Chip, IconButton, Stack, Switch, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import { formatDate } from 'utils/helperFunctions';
import { DatasetResponse } from 'apis/business/setting/knowledgeBase/types';
import { FaPlus } from 'react-icons/fa6';
import {
  useDeleteAgentDataSet,
  useGetAgent,
  useGetAgentDataSets,
  useUpdateAgentDataSet
} from 'apis/business/agent/hook';
import * as urls from 'routes/config';
import RetrievalTestingButton from '@/pages/Business/Setting/DataSet/Components/RetrievalTestingButton';

export default function AgentDatasets({
  type,
  knowledgeBaseId
}: {
  type: 'website' | 'document';
  knowledgeBaseId?: any;
}) {
  const navigate = useNavigate();
  const { currentPage, perPage, setPage, setPerPage } = usePagination();

  const filter =
    type === 'website' ? { 'filter[datasource_type_eq]': 1 } : { 'filter[datasource_type_not_in][]': [1, 8] };
  const { data: dataSets, isLoading: isDataSetsLoading } = useGetAgentDataSets({
    ...filter,
    page: currentPage,
    per_page: perPage
  });
  const { mutate: deleteDataSet } = useDeleteAgentDataSet();
  const { mutateAsync: handleDataSet } = useUpdateAgentDataSet();

  const columns = useColumns(deleteDataSet, handleDataSet, type, navigate);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', lg: 'center' },
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 1.5, lg: 0 },
          marginBottom: '24px'
        }}
      >
        <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          {type === 'website' ? 'Web URL Datasets' : 'Documents Dataset'}
        </Typography>
        <Stack direction='row' gap={2}>
          <RetrievalTestingButton knowledgeBaseId={knowledgeBaseId} />

          <Button
            onClick={() => navigate(type === 'website' ? 'add-website' : 'add-document')}
            sx={{ mt: { xs: 1, lg: 0 }, width: { xs: '100%', sm: 'auto' } }}
          >
            <Box display='flex' alignItems='center' gap={1} justifyContent='center'>
              <FaPlus /> Add
            </Box>
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <DataTable data={dataSets?.data || []} columns={columns} isHeaderSticky={true} isLoading={isDataSetsLoading} />
      </Box>
      <Pagination
        totalPages={dataSets?.meta?.total_pages}
        totalCount={dataSets?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
    </Box>
  );
}

type DeleteFn = (id: string) => void;
type UpdateFn = (payload: any) => void;

const pillColor = {
  parsing: 'warning',
  success: 'success',
  failed: 'error',
  cancelled: 'error'
};

function useColumns(deleteDataSet: DeleteFn, handleDataSet: UpdateFn, type: 'website' | 'document', navigate: any) {
  const { data: agent } = useGetAgent();

  return useMemo<ColumnDef<DatasetResponse>[]>(
    () => [
      {
        header: 'Page Title',
        size: 100,
        accessorKey: 'name',
        cell: (info) => (
          <Typography
            title={info.row.original.name ?? '-'}
            sx={{
              maxWidth: { xs: 120, lg: 180 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {info.row.original.name ?? '-'}
          </Typography>
        )
      },
      {
        header: 'Page Url',
        size: 100,
        accessorKey: 'file_url',
        cell: (info) => {
          const pageUrl =
            (info.row.original as DatasetResponse & { page_url?: string }).page_url || info.row.original.file_url;

          return (
            <Typography
              title={pageUrl ?? '-'}
              sx={{
                maxWidth: { xs: 150, lg: 260 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {pageUrl ?? '-'}
            </Typography>
          );
        }
      },
      {
        header: 'Questions',
        size: 50,
        accessorKey: 'chunks_count'
      },
      {
        header: 'Created At',
        size: 100,
        accessorKey: 'upload_date',
        cell: (info) => <Typography>{formatDate(info.row.original.created_at)}</Typography>
      },
      {
        header: 'ENABLE',
        size: 50,
        accessorKey: 'enable',
        cell: (info) => (
          <Switch
            defaultChecked={Boolean(info.getValue())}
            onChange={(e) =>
              handleDataSet({
                datasetId: info.row.original.id,
                payload: { dataset: { enable: e.target.checked } }
              })
            }
          />
        )
      },
      {
        header: 'STATUS',
        size: 50,
        cell: (info) => (
          <Chip
            label={info.row.original.status}
            variant='outlined'
            color={pillColor[info.row.original.status || 'failed'] as 'success' | 'warning' | 'error'}
            sx={{
              paddingY: '4px',
              paddingX: '12px',
              cursor: 'help',
              borderRadius: '100px'
            }}
          />
        )
      },
      {
        header: 'Action',
        size: 20,
        cell: (info) => (
          <Box
            display='flex'
            alignItems='center'
            sx={{
              flexWrap: { xs: 'wrap', lg: 'nowrap' }
            }}
          >
            <IconButton
              onClick={() => navigate(`${agent?.default_kb_id}/dataset/${urls.configure.chunks(info.row.original.id)}`)}
            >
              <MdOutlineEdit />
            </IconButton>

            <IconButton
              onClick={() => deleteDataSet(String(info.row.original.id))}
              color='error'
              sx={{
                p: { xs: 0.5, lg: 1 }
              }}
            >
              <RiDeleteBin6Line style={{ color: 'red' }} />
            </IconButton>
          </Box>
        )
      }
    ],
    [deleteDataSet, handleDataSet, agent, navigate]
  );
}
